import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { type BrowserHistory, createBrowserHistory } from 'history'
import { isClient } from '@/utilities'

export interface QueryStringClient {
  get: () => string
  getValue: (queryParamName: string) => string | null
  set: (value: string) => void
  remove: (queryParamName: string) => void
  replaceValue: (queryParamName: string, value: string) => void
}

export function createQueryStringClient({
  initialHistory,
  frameworkRouter,
  initialWindow,
}: {
  initialHistory?: BrowserHistory
  frameworkRouter?: AppRouterInstance
  initialWindow?: Window
} = {}): QueryStringClient {
  let browserHistory: BrowserHistory
  let globalWindow: Window

  if (isClient()) {
    globalWindow = initialWindow || window
    browserHistory = initialHistory || createBrowserHistory({ window: globalWindow })
  }

  function get() {
    if (!globalWindow)
      return ''

    return globalWindow.location.search
  }

  function getPathname() {
    return globalWindow.location.pathname.replace(/^\/+/, '/')
  }

  function set(queryString: string) {
    if (frameworkRouter) {
      frameworkRouter.push(`${getPathname()}?${queryString}`, { scroll: false })
      return
    }

    if (!browserHistory)
      return

    browserHistory.push({
      pathname: getPathname(),
      search: `?${queryString}`,
    })
  }

  function getValue(queryParamName: string): string | null {
    const paramValue = new URLSearchParams(get()).get(queryParamName)
    return paramValue || null
  }

  function remove(queryParamName: string) {
    const urlParams = new URLSearchParams(get())
    urlParams.delete(queryParamName)
    browserHistory.replace(`${getPathname()}?${urlParams.toString()}`)
  }

  function replaceValue(queryParamName: string, value: string) {
    if (!globalWindow)
      return

    const urlParams = new URLSearchParams(get())
    urlParams.delete(queryParamName)
    urlParams.set(queryParamName, value)
    const search = urlParams.toString()
    const { hash } = globalWindow.location
    const { history } = globalWindow

    history.replaceState(history.state, '', `${getPathname()}${search ? `?${search}` : ''}${hash}`)
  }

  return { get, set, getValue, remove, replaceValue }
}

export default createQueryStringClient()
